import { yupResolver } from "@hookform/resolvers/yup"
import {
  Accordion,
  Button,
  FileInput,
  Group,
  List,
  LoadingOverlay,
  Modal,
  Select,
  Tabs,
  Textarea,
  TextInput,
  ThemeIcon,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useDisclosure } from "@mantine/hooks"
import {
  IconArchive,
  IconArrowBackUp,
  IconCalendar,
  IconCurrencyDollar,
  IconDeviceFloppy,
  IconFidgetSpinner,
  IconFileDollar,
  IconIndentIncrease,
  IconStatusChange,
  IconTrash,
  IconUpload,
  IconUser,
} from "@tabler/icons-react"
import dayjs from "dayjs"
import { debounce } from "lodash-es"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import * as yup from "yup"
import { CollectionQuery } from "../../../models/collection.model"
import {
  Lease,
  LeaseStatus,
  LeaseType,
  PaymentFrequency,
} from "../../../models/lease.model"
import { Property } from "../../../models/property.model"
import { Tenant } from "../../../models/tenant.model"
import { Unit } from "../../../models/unit.model"
import ActivityLogWrapperComponent from "../../../shared/component/ActivityLog/activityLog-wrapper-component"
import Confirmation from "../../../shared/component/confirmation/action-confirmation"
import { Constants } from "../../../shared/constants/Constants"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { useLazyGetActivitiesQuery } from "../../../shared/store/query/sharedQuery"
import { onViewFile } from "../../../shared/utility/view-file"
import {
  useArchiveLeaseMutation,
  useCreateLeaseMutation,
  useDeleteLeaseMutation,
  useLazyGetArchivedLeaseQuery,
  useLazyGetLeaseQuery,
  useLazyGetPropertiesOfLeaseQuery,
  useLazyGetTenantsOfLeaseQuery,
  useLazyGetUnitsOfLeaseQuery,
  usePayForLeaseManuallyMutation,
  usePayForTenantLeaseManuallyMutation,
  useRemoveLeaseFileMutation,
  useRestoreLeaseMutation,
  useToggleLeaseStatusMutation,
  useUpdateLeaseMutation,
} from "../store/lease.query"
import { RentIncreaseComponent } from "./rent-increase.component"
import { PaymentComponent } from "./payment.component"
import { FileImage } from "lucide-react"
import FileViewerModal from "../../../shared/component/file-viewer/file-viewer-modal.component"
import { notification } from "../../../shared/component/notification/utility/notification"
import { MoonLoader, PulseLoader } from "react-spinners"
import LoadingInputWrapper from "../../../shared/component/LoadingInputWrapper"
import { ManagementFeeIncreaseComponent } from "./management-fee-increase.component"
interface Props {
  editMode: string
}

export default function NewLeaseComponent(props: Props) {
  const { editMode } = props
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const [opened, { open, close }] = useDisclosure()
  const [viewOpened, { open: viewOpen, close: viewClose }] = useDisclosure()
  const [leaseStatus, setLeaseStatus] = useState<LeaseStatus>(
    LeaseStatus.Pending,
  )
  const [fileInput, setFileInput] = useState<File | null>(null)
  const [paymentFrequency, setPaymentFrequency] = useState<PaymentFrequency>()
  const [fsNumber, setFsNumber] = useState<string>("")
  const [selectedFiles, setSelectedFiles] = useState<string[]>()

  const [propertySearch, setPropertySearch] = useState<string | null>(null)
  const [unitSearch, setUnitSearch] = useState<string | null>(null)
  const [tenantSearch, setTenantSearch] = useState<string | null>(null)
  const [ownersCollection, setOwnersCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
  })
  const [propertiesCollection, setPropertiesCollection] =
    useState<CollectionQuery>({
      top: 10,
      skip: 0,
    })
  const [unitCollection, setUnitCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
  })
  const [tenantsCollection, setTenantsCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
  })

  let schema: any = yup.object().shape({
    // startDate: yup
    //   .string()
    //   .typeError(
    //     t("typeValidation", { type: t("string"), field: t("startDate") }),
    //   )
    //   .required(t("requiredValidation", { field: t("startDate") })),
    // endDate: yup
    //   .string()
    //   .typeError(
    //     t("typeValidation", { type: t("string"), field: t("endDate") }),
    //   )
    //   .required(t("requiredValidation", { field: t("endDate") })),
    rentAmount: yup
      .number()
      .typeError(
        t("typeValidation", { type: t("number"), field: t("rentAmount") }),
      )
      .min(1, t("minValidation", { field: t("rentAmount"), count: 1 }))
      .required(t("requiredValidation", { field: t("rentAmount") })),
    // securityDeposit: yup
    //   .number()
    //   .typeError(
    //     t("typeValidation", {
    //       field: t("securityDeposit"),
    //       type: t("number"),
    //     }),
    //   )
    //   .min(0, t("minValidation", { field: t("securityDeposit"), count: 0 }))
    //   .required(t("requiredValidation", { field: t("securityDeposit") })),
    paymentFrequency: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("paymentFrequency"),
          type: t("string"),
        }),
      )
      .oneOf([
        PaymentFrequency.Monthly,
        PaymentFrequency.Annually,
        PaymentFrequency.Quarterly,
        PaymentFrequency.SixMonths,
      ])
      .required(t("requiredValidation", { field: t("paymentFrequency") })),
    // lateFeeAmount: yup
    //   .number()
    //   .typeError(
    //     t("typeValidation", {
    //       field: t("lateFee"),
    //       type: t("number"),
    //     }),
    //   )
    //   .min(0, t("minValidation", { field: t("lateFee"), count: 0 }))
    //   .nullable(),
    terminationNotice: yup
      .number()
      .typeError(
        t("typeValidation", {
          field: t("terminationNotice"),
          type: t("number"),
        }),
      )
      .min(0, t("minValidation", { field: t("terminationNotice"), count: 0 }))
      .nullable(),
    notes: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("notes"),
          type: t("string"),
        }),
      )
      .nullable(),
    propertyId: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("property", { count: 1 }),
          type: t("string"),
        }),
      )
      .required(
        t("requiredValidation", { field: t("property", { count: 1 }) }),
      ),
    unitId: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("unit", { count: 1 }),
          type: t("string"),
        }),
      )
      .required(t("requiredValidation", { field: t("unit", { count: 1 }) })),
    tenantId: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("tenant", { count: 1 }),
          type: t("string"),
        }),
      )
      .required(t("requiredValidation", { field: t("tenant", { count: 1 }) })),
    // lease: yup
    //   .mixed()
    //   .nullable()
    //   .when([], (boloDueDate: any, schema: any) => {
    //     if (editMode == "new" && !lease?.data?.miniolease) {
    //       return schema
    //         .nullable()
    //         .test(
    //           "fileSize",
    //           t("sizeValidation", {
    //             field: t("lease", { count: 1 }),
    //             size: "2M",
    //           }),
    //           (value: any) => {
    //             if (!value) return true
    //             return value && value?.size <= 2000000
    //           },
    //         )
    //         .test(
    //           "type",
    //           t("allowedValidation", { allowed: "pdf, png, jpg, jpeg" }),
    //           (value: any) => {
    //             if (!value) return true
    //             return (
    //               (value && value.type === "application/pdf") ||
    //               (value && value.type === "image/png") ||
    //               (value && value.type === "image/jpeg") ||
    //               (value && value.type === "image/jpg")
    //             )
    //           },
    //         )
    //     }
    //   }),
  })

  const defaultValue: Lease = {
    startDate: null,
    endDate: null,
    rentAmount: 0,
    securityDepositAmount: 0,
    paymentFrequency: PaymentFrequency.Monthly,
    leaseStatus: LeaseStatus.Pending,
    leaseType: LeaseType.MonthToMonth,
    paymentDueDate: null,
    lateFeeAmount: 0.0,
    lateFeeType: "",
    gracePeriod: 10,
    utilityFee: 0.0,
    terminationNotice: 0,
    terminatedAt: null,
    autoRenewal: false,
    notes: "",
  }

  const [getLease, lease] = useLazyGetLeaseQuery()
  const [createLease, newLease] = useCreateLeaseMutation()
  const [updateLease, updateResponse] = useUpdateLeaseMutation()
  const [restoreLease, restoredLease] = useRestoreLeaseMutation()
  const [deleteLease, deleteResponse] = useDeleteLeaseMutation()
  const [getActivities, activities] = useLazyGetActivitiesQuery()
  const [archiveLease, archiveResponse] = useArchiveLeaseMutation()
  const [toggleStatus, toggleStatusResponse] = useToggleLeaseStatusMutation()
  const [getArchivedLease, archivedLease] = useLazyGetArchivedLeaseQuery()
  const [getProperties, properties] = useLazyGetPropertiesOfLeaseQuery()
  const [getUnits, units] = useLazyGetUnitsOfLeaseQuery()
  const [getTenants, tenants] = useLazyGetTenantsOfLeaseQuery()

  const [payManually, payManuallyResponse] = usePayForLeaseManuallyMutation()
  const [payTenantManually, payTenantManuallyResponse] =
    usePayForTenantLeaseManuallyMutation()
  const [removeLeaseFile, removeLeaseFileResponse] =
    useRemoveLeaseFileMutation()

  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<Lease>({
    resolver: yupResolver(schema),
    mode: "all",
  })

  const currentUser: any = JSON.parse(
    localStorage.getItem("userInfo") as string,
  )
  const propertyWatch = watch("propertyId")
  const securityDepositWatch = watch("securityDepositType")

  useEffect(() => {
    if (editMode === "detail" && params?.id) {
      if (params?.type && params?.type === "archived") {
        getArchivedLease(`${params?.id}`).then((response: any) => {
          reset({
            ...response.data,
            startDate: response?.data?.startDate
              ? new Date(response?.data?.startDate)
              : null,
            endDate: response?.data?.endDate
              ? new Date(response?.data?.endDate)
              : null,
            terminatedAt: response?.data?.terminatedAt
              ? new Date(response?.data?.endDate)
              : null,
            paymentDueDate: response?.data?.paymentDueDate
              ? new Date(response?.data?.endDate)
              : null,
          })
        })
      } else {
        getLease(`${params?.id}`).then((response: any) => {
          reset({
            ...response.data,
            startDate: response?.data?.startDate
              ? new Date(response?.data?.startDate)
              : null,
            endDate: response?.data?.endDate
              ? new Date(response?.data?.endDate)
              : null,
            paymentDueDate: response?.data?.paymentDueDate
              ? new Date(response?.data?.paymentDueDate)
              : null,
            terminatedAt: response?.data?.terminatedAt
              ? new Date(response?.data?.terminatedAt)
              : null,
            // paymentDueDate: response?.data?.paymentDueDate
            //   ? new Date(response?.data?.paymentDueDate)
            //   : null,
            internalEndDate: response?.data?.internalEndDate
              ? new Date(response?.data?.internalEndDate)
              : null,
            internalStartDate: response?.data?.internalStartDate
              ? new Date(response?.data?.internalStartDate)
              : null,
            discountEndDate: response?.data?.discountEndDate
              ? new Date(response?.data?.discountEndDate)
              : null,
          })
          setLeaseStatus(response?.data?.leaseStatus as LeaseStatus)
          setValue("lease", null)
        })
      }
    } else {
      const searchParams = new URLSearchParams(location.search)

      if (searchParams.get("leaseId")) {
        getLease(`${searchParams.get("leaseId")}`).then((response: any) => {
          reset({
            ...response.data,
            startDate: response?.data?.startDate
              ? new Date(response?.data?.startDate)
              : null,
            endDate: response?.data?.endDate
              ? new Date(response?.data?.endDate)
              : null,
            internalStartDate: response?.data?.internalStartDate
              ? new Date(response?.data?.internalStartDate)
              : null,
            internalEndDate: response?.data?.internalEndDate
              ? new Date(response?.data?.internalEndDate)
              : null,
            discountEndDate: response?.data?.discountEndDate
              ? new Date(response?.data?.discountEndDate)
              : null,
            terminatedAt: response?.data?.terminatedAt
              ? new Date(response?.data?.endDate)
              : null,
            paymentDueDate: response?.data?.paymentDueDate
              ? new Date(response?.data?.endDate)
              : null,
          })
          setLeaseStatus(response?.data?.leaseStatus as LeaseStatus)
          setValue("lease", null)
          setValue("securityDepositType", "flat")
        })
      } else {
        reset(defaultValue)
        setValue("securityDepositType", "flat")
      }
    }
  }, [params, editMode, getLease])

  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getProperties(propertiesCollection)
    } else {
      getProperties({
        ...propertiesCollection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [propertiesCollection])
  useEffect(() => {
    if (propertyWatch) {
      getUnits({
        ...unitCollection,
        filter: [
          [
            {
              field: "propertyId",
              operator: "=",
              value: propertyWatch,
            },
          ],
          [
            {
              field: "enabled",
              operator: "=",
              value: true,
            },
          ],
        ],
      })
    }
  }, [unitCollection, propertyWatch])

  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getTenants(tenantsCollection)
    } else {
      getTenants({
        ...tenantsCollection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [tenantsCollection])

  useEffect(() => {
    if (tenantSearch) {
      if (userInfo?.role?.key == "super_admin") {
        getTenants({
          ...tenantsCollection,
          filter: [
            [
              { field: "name", operator: "like", value: tenantSearch },
              { field: "phoneNumber", operator: "like", value: tenantSearch },
            ],
          ],
        })
      } else {
        getTenants({
          ...tenantsCollection,
          filter: [
            [
              { field: "name", operator: "like", value: tenantSearch },
              { field: "phoneNumber", operator: "like", value: tenantSearch },
            ],
            [
              {
                field: "ownerId",
                operator: "=",
                value: ownerInfo?.parentOwnerId,
              },
            ],
          ],
        })
      }
    }
  }, [tenantSearch])
  useEffect(() => {
    if (propertySearch) {
      if (userInfo?.role?.key == "super_admin") {
        getProperties({
          ...propertiesCollection,
          filter: [
            [{ field: "name", operator: "like", value: propertySearch }],
          ],
        })
      } else {
        getProperties({
          ...propertiesCollection,
          filter: [
            [{ field: "name", operator: "like", value: propertySearch }],
            [
              {
                field: "ownerId",
                operator: "=",
                value: ownerInfo?.parentOwnerId,
              },
            ],
          ],
        })
      }
    }
  }, [propertySearch])
  useEffect(() => {
    if (propertyWatch && unitSearch) {
      getUnits({
        ...unitCollection,
        filter: [
          [{ field: "name", operator: "like", value: unitSearch }],
          [{ field: "propertyId", operator: "=", value: propertyWatch }],
          [{ field: "enabled", operator: "=", value: true }],
        ],
      })
    }
  }, [unitSearch])

  function onSubmit(data: any) {
    if (currentUser?.role?.key != "super_admin") {
      data = { ...data, ownerId: currentUser?.id }
    }
    data.terminationNotice = +data?.terminationNotice

    if (data.startDate) {
      data.startDate = data?.startDate ? new Date(data?.startDate) : null
    }

    if (data.endDate) {
      data.endDate = data?.endDate ? new Date(data?.endDate) : null
    }
    // data.endDate = data?.endDate ? new Date(data?.endDate) : null
    data.discountEndDate = data?.discountEndDate
      ? new Date(data?.discountEndDate)
      : null
    data.internalEndDate = data?.internalEndDate
      ? new Date(data?.internalEndDate)
      : null
    data.internalStartDate = data?.internalStartDate
      ? new Date(data?.internalStartDate)
      : null
    data.paymentDueDate = data?.paymentDueDate
      ? new Date(data?.paymentDueDate)
      : null

    const formData = new FormData()

    if (data.startDate) {
      formData.append("startDate", dayjs(data?.startDate).format())
    }
    if (data.endDate) {
      formData.append("endDate", dayjs(data?.endDate).format())
    }
    if (data.rentAmount) {
      formData.append("rentAmount", data?.rentAmount)
    }
    // formData.append("securityDeposit", data?.securityDeposit)
    if (data.paymentFrequency) {
      formData.append("paymentFrequency", data?.paymentFrequency)
    }
    if (data.paymentDueDate) {
      formData.append("paymentDueDate", dayjs(data?.paymentDueDate).format())
    }
    if (data.leaseType) {
      formData.append("leaseType", data?.leaseType)
    }
    if (data.lateFee) {
      formData.append("lateFee", data?.lateFee)
    }
    if (data.utilityFee) {
      formData.append("utilityFee", data?.utilityFee)
    }
    if (data.terminationNotice) {
      formData.append("terminationNotice", data?.terminationNotice)
    }
    if (data.autoRenewal) {
      formData.append("autoRenewal", data?.autoRenewal)
    }
    if (data.gracePeriod) {
      formData.append("gracePeriod", data?.gracePeriod)
    }
    if (data.notes) {
      formData.append("notes", data?.notes ?? "")
    }
    if (data.lateFeeType) {
      formData.append("lateFeeType", data?.lateFeeType)
    }

    if (data.lateFeeAmount) {
      formData.append("lateFeeAmount", data?.lateFeeAmount)
    }
    // if (data.securityDeposit) formData.append("securityDeposit", data?.securityDeposit)
    if (data.securityDepositType)
      formData.append("securityDepositType", data?.securityDepositType)
    if (data.securityDepositAmount)
      formData.append("securityDepositAmount", data?.securityDepositAmount)
    if (data.securityDeposit && data.securityDeposit.length > 0) {
      data.securityDeposit.forEach((file: any) => {
        formData.append("securityDeposit", file)
      })
    }
    if (data.lease && data.lease.length > 0) {
      data.lease.forEach((file: any) => {
        formData.append("lease", file)
      })
    }
    formData.append("propertyId", data?.propertyId)
    formData.append("tenantId", data?.tenantId)
    formData.append("unitId", data?.unitId)
    data?.discountType && formData.append("discountType", data?.discountType)
    data?.discountAmount &&
      formData.append("discountAmount", data?.discountAmount)
    data?.discountEndDate &&
      formData.append("discountEndDate", dayjs(data?.discountEndDate).format())

    data?.internalStartDate &&
      formData.append(
        "internalStartDate",
        dayjs(data?.internalStartDate).format(),
      )
    data?.internalEndDate &&
      formData.append("internalEndDate", dayjs(data?.internalEndDate).format())

    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get("leaseId")) {
      formData.append("previousLeaseId", searchParams.get("leaseId") as string)
    }

    try {
      if (editMode === "new") {
        createLease(formData).then((response: any) => {
          if (!response?.error) {
            reset(defaultValue)
          }
        })
      } else {
        formData.append("id", params?.id as string)
        updateLease(formData)
      }
    } catch (e: any) {
      // console.log("🚀 ~ onSubmit ~ e:", e)
    }
  }

  const onError = (error: any) => {
    // console.log("🚀 ~ onError ~ error:", error)
  }

  function handleFileRemoval(item: string) {
    removeLeaseFile({
      leaseId: lease?.data?.id as string,
      filename: item,
    }).then((response) => {
      notification("success", "File removed Successfully")
      viewClose()
    })
  }
  function _parseProperties() {
    let data: { label: string; value: string }[] = []

    properties?.data?.data?.map((property: Property) => {
      data.push({
        label: property?.name as string,
        value: property?.id as string,
      })
    })

    if (
      lease?.data?.property &&
      data.some((item) => item.value !== lease?.data?.propertyId)
    ) {
      data.unshift({
        label: lease?.data?.property?.name as string,
        value: lease?.data?.propertyId as string,
      })
    }
    return data
  }
  function _parseUnits() {
    let data: { label: string; value: string }[] = []

    units?.data?.data?.map((unit: Unit) => {
      data.push({ label: unit?.name as string, value: unit?.id as string })
    })

    if (
      lease?.data?.unit &&
      data.some((item) => item.value !== lease?.data?.unitId)
    ) {
      data.unshift({
        label: lease?.data?.unit?.name as string,
        value: lease?.data?.unitId as string,
      })
    }

    return data
  }
  function _parseTenants() {
    let data: { label: string; value: string }[] = []

    tenants?.data?.data?.map((tenant: Tenant) => {
      data.push({
        label: `${tenant?.name as string} (${tenant?.phoneNumber as string})`,
        value: tenant?.id as string,
      })
    })

    if (
      lease?.data?.tenant &&
      data.some((item) => item.value !== lease?.data?.tenantId)
    ) {
      data.unshift({
        label: lease?.data?.tenant?.name as string,
        value: lease?.data?.tenantId as string,
      })
    }

    return data
  }
  function _parseLeaseStatus() {
    let data: { label: string; value: string }[] = []

    if (lease?.data?.leaseStatus === LeaseStatus.Active) {
      data.push({ label: t("expired"), value: LeaseStatus.Expired })
      data.push({ label: t("terminated"), value: LeaseStatus.Terminated })
    } else {
      data.push({ label: t("expired"), value: LeaseStatus.Expired })
      data.push({ label: t("terminated"), value: LeaseStatus.Terminated })
    }

    return data
  }

  return (
    // <div className="h-full relative w-full dark:bg-gray-700 dark:text-white flex space-x-2 justify-center">
    <ActivityLogWrapperComponent
      title={
        editMode === "new" ? (
          t("new") + " " + t("lease", { count: 1 })
        ) : (
          <div className="h-full space-x-2 flex items-center">
            <div>{lease?.data?.name}</div>
          </div>
        )
      }
      item={lease}
      path={`/lease/detail/${params?.type}/${params.id}`}
      id={params.id ?? ""}
    >
      <LoadingOverlay
        visible={
          newLease.isLoading ||
          updateResponse.isLoading ||
          lease.isLoading ||
          lease.isFetching
        }
      />
      <>
        <Tabs defaultValue="Lease">
          <Tabs.List>
            <Tabs.Tab value="Lease" icon={<IconUser size={14} />}>
              {t("leaseInfo")}
            </Tabs.Tab>

            {editMode == "detail" && (
              <>
                <Tabs.Tab
                  value="rent-increase"
                  icon={<IconIndentIncrease size={14} />}
                >
                  {t("rentIncrease")}
                </Tabs.Tab>
                <Tabs.Tab
                  value="management-fee-increase"
                  icon={<IconIndentIncrease size={14} />}
                >
                  {t("managementFeeIncrease")}
                </Tabs.Tab>
                <Tabs.Tab value="payment" icon={<IconFileDollar size={14} />}>
                  {t("payment", { count: 2 })}
                </Tabs.Tab>
              </>
            )}
          </Tabs.List>

          <Tabs.Panel value="Lease" pt="xs">
            <div className="flex  justify-center h-full">
              <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="w-full gap-y-4 dark:text-white"
              >
                <Accordion
                  defaultValue={["info", "address", "emergency"]}
                  multiple
                  variant="separated"
                  classNames={{
                    control:
                      "bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg rounded-xl border-none md:border md:border-gray-700",
                  }}
                >
                  <Accordion.Item value="info">
                    <Accordion.Control>{t("leaseInfo")}</Accordion.Control>
                    <Accordion.Panel className="bg-gray-800">
                      <div className="flex-col space-y-4">
                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <LoadingInputWrapper isLoading={tenants.isFetching}>
                            <Controller
                              control={control}
                              name="tenantId"
                              disabled={userInfo?.role?.key === "tenant"}
                              render={({ field }) => (
                                <Select
                                  classNames={{ label: "dark:text-white" }}
                                  className="w-full"
                                  onKeyDown={debounce(
                                    (event: any) =>
                                      setTenantSearch(event?.target?.value),
                                    1000,
                                  )}
                                  searchable
                                  clearable
                                  withAsterisk
                                  error={
                                    errors.tenantId &&
                                    `${errors?.tenantId?.message}`
                                  }
                                  label={t("tenant", { count: 1 })}
                                  placeholder={t("tenant", { count: 1 })}
                                  data={_parseTenants()}
                                  {...field}
                                />
                              )}
                            />
                          </LoadingInputWrapper>
                          <LoadingInputWrapper
                            isLoading={properties.isFetching}
                          >
                            <Controller
                              control={control}
                              name="propertyId"
                              disabled={userInfo?.role?.key === "tenant"}
                              render={({ field }) => (
                                <Select
                                  classNames={{ label: "dark:text-white" }}
                                  className="w-full"
                                  onKeyDown={debounce(
                                    (event: any) =>
                                      setPropertySearch(event?.target?.value),
                                    1000,
                                  )}
                                  searchable
                                  clearable
                                  withAsterisk
                                  error={
                                    errors.propertyId &&
                                    `${errors?.propertyId?.message}`
                                  }
                                  label={t("property", { count: 1 })}
                                  placeholder={t("property", { count: 1 })}
                                  data={_parseProperties()}
                                  {...field}
                                />
                              )}
                            />
                          </LoadingInputWrapper>
                        </div>

                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <LoadingInputWrapper
                            isLoading={units.isFetching}
                            topPadding="mt-11"
                          >
                            <Controller
                              control={control}
                              name="unitId"
                              disabled={userInfo?.role?.key === "tenant"}
                              render={({ field }) => (
                                <Select
                                  classNames={{ label: "dark:text-white" }}
                                  className="w-full"
                                  withAsterisk
                                  onKeyDown={debounce(
                                    (event: any) =>
                                      setUnitSearch(event?.target?.value),
                                    1000,
                                  )}
                                  searchable
                                  clearable
                                  error={
                                    errors.unitId &&
                                    `${errors?.unitId?.message}`
                                  }
                                  description={t("unitDesc")}
                                  label={t("unit", { count: 1 })}
                                  placeholder={t("unit", { count: 1 })}
                                  data={_parseUnits()}
                                  {...field}
                                />
                              )}
                            />
                          </LoadingInputWrapper>

                          <Controller
                            control={control}
                            disabled={userInfo?.role?.key === "tenant"}
                            name="paymentFrequency"
                            render={({ field }) => (
                              <Select
                                classNames={{ label: "dark:text-white mb-5" }}
                                className="w-full"
                                clearable
                                withAsterisk
                                error={
                                  errors.paymentFrequency &&
                                  `${errors?.paymentFrequency?.message}`
                                }
                                label={t("paymentFrequency")}
                                placeholder={t("paymentFrequency")}
                                data={[
                                  PaymentFrequency.Monthly,
                                  PaymentFrequency.Quarterly,
                                  PaymentFrequency.SixMonths,
                                  PaymentFrequency.Annually,
                                ]}
                                {...field}
                              />
                            )}
                          />
                        </div>

                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            disabled={userInfo?.role?.key === "tenant"}
                            className="w-full"
                            type="number"
                            error={
                              errors.terminationNotice &&
                              `${errors?.terminationNotice?.message}`
                            }
                            label={t("terminationNotice")}
                            placeholder={t("terminationNotice")}
                            {...register("terminationNotice")}
                          />

                          <div className="w-full">
                            <Controller
                              control={control}
                              name="lease"
                              disabled={userInfo?.role?.key === "tenant"}
                              render={({ field }) => (
                                <FileInput
                                  // withAsterisk
                                  clearable
                                  multiple
                                  // className="w-full"
                                  error={
                                    errors.lease && `${errors.lease?.message}`
                                  }
                                  label={t("lease", { count: 1 })}
                                  //@ts-ignore
                                  placeholder={t("lease", { count: 1 })}
                                  icon={<IconUpload size={14} />}
                                  {...field}
                                />
                              )}
                            />
                            {lease?.data?.minioLeases &&
                              lease?.data?.minioLeases?.length > 0 && (
                                <span
                                  className="cursor-pointer text-primary font-bold my-2 hover:underline"
                                  onClick={() => {
                                    setSelectedFiles(
                                      lease?.data?.minioLeases as string[],
                                    )
                                    viewOpen()
                                  }}
                                >
                                  {/* {t("viewLeaseFile")} */}
                                  <p>
                                    View (
                                    {lease?.data?.minioLeases?.length ?? 0}
                                    )files.
                                  </p>
                                </span>
                              )}
                          </div>
                          {/* <Controller

                              control={control}
                              name="leaseStatus"
                              render={({ field }) => (
                                <Select
                                  classNames={{ label: "dark:text-white" }}
                                  className="w-full"
                                  clearable
                                  withAsterisk

                                  error={
                                    errors.leaseStatus && `${errors?.leaseStatus?.message}`
                                  }
                                  label="Lease Status"
                                  placeholder="Select Lease Status"
                                  data={[
                                    LeaseStatus.Active,
                                    LeaseStatus.Expired,
                                    LeaseStatus.Pending,
                                    LeaseStatus.Terminated
                                  ]}
                                  {...field}

                                />
                              )}

                            /> */}
                        </div>

                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <Controller
                            control={control}
                            name="startDate"
                            disabled={userInfo?.role?.key === "tenant"}
                            render={({ field }) => (
                              <DateInput
                                icon={<IconCalendar size={16} />}
                                className="w-full mt-3"
                                {...field}
                                // excludeDate={(edate) => {
                                //   if (edate.getFullYear() < date.getFullYear()) {
                                //     return true;
                                //   } else if (
                                //     edate.getFullYear() === date.getFullYear() &&
                                //     edate.getMonth() < date.getMonth()
                                //   ) {
                                //     return true;
                                //   } else {
                                //     if (
                                //       edate.getFullYear() <= date.getFullYear() &&
                                //       edate.getMonth() === date.getMonth() &&
                                //       edate.getDate() <= date.getDate()
                                //     ) {
                                //       return true;
                                //     } else {
                                //       return false;
                                //     }
                                //   }
                                // }}
                                error={
                                  errors.startDate &&
                                  `${errors.startDate?.message}`
                                }
                                label={t("startDate")}
                                // withAsterisk
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name="endDate"
                            disabled={userInfo?.role?.key === "tenant"}
                            render={({ field }) => (
                              <DateInput
                                icon={<IconCalendar size={16} />}
                                className="w-full mt-3"
                                {...field}
                                // excludeDate={(edate) => {
                                //   if (edate.getFullYear() < date.getFullYear()) {
                                //     return true;
                                //   } else if (
                                //     edate.getFullYear() === date.getFullYear() &&
                                //     edate.getMonth() < date.getMonth()
                                //   ) {
                                //     return true;
                                //   } else {
                                //     if (
                                //       edate.getFullYear() <= date.getFullYear() &&
                                //       edate.getMonth() === date.getMonth() &&
                                //       edate.getDate() <= date.getDate()
                                //     ) {
                                //       return true;
                                //     } else {
                                //       return false;
                                //     }
                                //   }
                                // }}

                                error={
                                  errors.endDate && `${errors.endDate?.message}`
                                }
                                label={t("endDate")}
                                // withAsterisk
                              />
                            )}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <Controller
                            control={control}
                            name="internalStartDate"
                            disabled={userInfo?.role?.key === "tenant"}
                            render={({ field }) => (
                              <DateInput
                                icon={<IconCalendar size={16} />}
                                className="w-full mt-3"
                                {...field}
                                // excludeDate={(edate) => {
                                //   if (edate.getFullYear() < date.getFullYear()) {
                                //     return true;
                                //   } else if (
                                //     edate.getFullYear() === date.getFullYear() &&
                                //     edate.getMonth() < date.getMonth()
                                //   ) {
                                //     return true;
                                //   } else {
                                //     if (
                                //       edate.getFullYear() <= date.getFullYear() &&
                                //       edate.getMonth() === date.getMonth() &&
                                //       edate.getDate() <= date.getDate()
                                //     ) {
                                //       return true;
                                //     } else {
                                //       return false;
                                //     }
                                //   }
                                // }}
                                error={
                                  errors.internalStartDate &&
                                  `${errors.internalStartDate?.message}`
                                }
                                label={t("internalStartDate")}
                                withAsterisk
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name="internalEndDate"
                            disabled={userInfo?.role?.key === "tenant"}
                            render={({ field }) => (
                              <DateInput
                                icon={<IconCalendar size={16} />}
                                className="w-full mt-3"
                                {...field}
                                // excludeDate={(edate) => {
                                //   if (edate.getFullYear() < date.getFullYear()) {
                                //     return true;
                                //   } else if (
                                //     edate.getFullYear() === date.getFullYear() &&
                                //     edate.getMonth() < date.getMonth()
                                //   ) {
                                //     return true;
                                //   } else {
                                //     if (
                                //       edate.getFullYear() <= date.getFullYear() &&
                                //       edate.getMonth() === date.getMonth() &&
                                //       edate.getDate() <= date.getDate()
                                //     ) {
                                //       return true;
                                //     } else {
                                //       return false;
                                //     }
                                //   }
                                // }}

                                error={
                                  errors.internalEndDate &&
                                  `${errors.internalEndDate?.message}`
                                }
                                label={t("internalEndDate")}
                                withAsterisk
                              />
                            )}
                          />
                        </div>

                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            disabled={userInfo?.role?.key === "tenant"}
                            error={
                              errors.rentAmount &&
                              `${errors?.rentAmount?.message}`
                            }
                            withAsterisk
                            type="number"
                            label={t("rentAmount")}
                            placeholder={t("rentAmount")}
                            {...register("rentAmount")}
                          />

                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            disabled={userInfo?.role?.key === "tenant"}
                            error={
                              errors.utilityFee &&
                              `${errors?.utilityFee?.message}`
                            }
                            // withAsterisk
                            type="number"
                            step={0.01}
                            label={t("administrativeFee")}
                            placeholder={t("administrativeFee")}
                            {...register("utilityFee")}
                          />
                        </div>

                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <Controller
                            control={control}
                            name="lateFeeType"
                            render={({ field }) => (
                              <Select
                                // withAsterisk
                                className="w-full"
                                error={
                                  errors?.lateFeeType &&
                                  `${errors?.lateFeeType?.message}`
                                }
                                radius={"xs"}
                                classNames={{
                                  label: "dark:text-white",
                                }}
                                label={t("lateFeeType")}
                                placeholder={t("lateFeeType")}
                                data={[
                                  { label: t("flat"), value: "flat" },
                                  {
                                    label: t("percent"),
                                    value: "percentage",
                                  },
                                ]}
                                maxDropdownHeight={400}
                                {...field}
                              />
                            )}
                          />
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            disabled={userInfo?.role?.key === "tenant"}
                            error={
                              errors.lateFeeAmount &&
                              `${errors?.lateFeeAmount?.message}`
                            }
                            // withAsterisk
                            type="number"
                            label={t("lateFee")}
                            placeholder={t("lateFee")}
                            {...register("lateFeeAmount")}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            disabled={userInfo?.role?.key === "tenant"}
                            error={
                              errors.gracePeriod &&
                              `${errors?.gracePeriod?.message}`
                            }
                            // withAsterisk
                            type="number"
                            step={0.01}
                            label={t("gracePeriod")}
                            placeholder={t("gracePeriod")}
                            {...register("gracePeriod")}
                          />

                          <Controller
                            control={control}
                            name="securityDepositType"
                            render={({ field }) => (
                              <Select
                                // withAsterisk
                                className="w-full"
                                error={
                                  errors?.securityDepositType &&
                                  `${errors?.securityDepositType?.message}`
                                }
                                radius={"xs"}
                                classNames={{
                                  label: "dark:text-white",
                                }}
                                label={t("securityDepositType")}
                                placeholder={t("securityDepositType")}
                                data={[
                                  { label: t("flat"), value: "flat" },
                                  {
                                    label: t("file"),
                                    value: "file",
                                  },
                                ]}
                                maxDropdownHeight={400}
                                {...field}
                              />
                            )}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          {securityDepositWatch &&
                          securityDepositWatch === "flat" ? (
                            <TextInput
                              classNames={{ label: "dark:text-white" }}
                              className="w-full"
                              disabled={userInfo?.role?.key === "tenant"}
                              error={
                                errors.securityDepositAmount &&
                                `${errors?.securityDepositAmount?.message}`
                              }
                              withAsterisk
                              label={t("securityDeposit")}
                              placeholder={t("securityDeposit")}
                              {...register("securityDepositAmount")}
                            />
                          ) : (
                            <div className="w-full">
                              <Controller
                                control={control}
                                name="securityDeposit"
                                disabled={userInfo?.role?.key === "tenant"}
                                render={({ field }) => (
                                  <FileInput
                                    // withAsterisk
                                    clearable
                                    multiple
                                    // className="w-full"
                                    error={
                                      errors.securityDeposit &&
                                      `${errors.securityDeposit?.message}`
                                    }
                                    label={t("securityDeposit")}
                                    //@ts-ignore
                                    placeholder={t("securityDeposit")}
                                    icon={<IconUpload size={14} />}
                                    {...field}
                                  />
                                )}
                              />
                              {lease?.data?.minioSecurityDeposits &&
                                lease?.data?.minioSecurityDeposits?.length >
                                  0 && (
                                  <span
                                    className="cursor-pointer text-primary font-bold my-2 hover:underline"
                                    onClick={() => {
                                      setSelectedFiles(
                                        lease?.data
                                          ?.minioSecurityDeposits as string[],
                                      )
                                      viewOpen()
                                    }}
                                  >
                                    {/* {t("viewLeaseFile")} */}
                                    <p>
                                      View (
                                      {
                                        lease?.data?.minioSecurityDeposits
                                          ?.length
                                      }
                                      ) files.
                                    </p>
                                  </span>
                                )}
                            </div>
                          )}

                          <Controller
                            control={control}
                            name="discountType"
                            render={({ field }) => (
                              <Select
                                // withAsterisk
                                className="w-full"
                                error={
                                  errors?.discountType &&
                                  `${errors?.discountType?.message}`
                                }
                                radius={"xs"}
                                classNames={{
                                  label: "dark:text-white",
                                }}
                                label={t("discountType")}
                                placeholder={t("discountType")}
                                data={[
                                  { label: t("flat"), value: "flat" },
                                  {
                                    label: t("percent"),
                                    value: "percentage",
                                  },
                                ]}
                                maxDropdownHeight={400}
                                {...field}
                              />
                            )}
                          />
                        </div>

                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            type="number"
                            error={
                              errors.discountAmount &&
                              `${errors?.discountAmount.message}`
                            }
                            label={t("discountAmount")}
                            // withAsterisk
                            placeholder={t("discountAmount")}
                            {...register("discountAmount")}
                          />
                          <Controller
                            control={control}
                            name="discountEndDate"
                            disabled={userInfo?.role?.key === "tenant"}
                            render={({ field }) => (
                              <DateInput
                                icon={<IconCalendar size={16} />}
                                className="w-full "
                                {...field}
                                // excludeDate={(edate) => {
                                //   if (edate.getFullYear() < date.getFullYear()) {
                                //     return true;
                                //   } else if (
                                //     edate.getFullYear() === date.getFullYear() &&
                                //     edate.getMonth() < date.getMonth()
                                //   ) {
                                //     return true;
                                //   } else {
                                //     if (
                                //       edate.getFullYear() <= date.getFullYear() &&
                                //       edate.getMonth() === date.getMonth() &&
                                //       edate.getDate() <= date.getDate()
                                //     ) {
                                //       return true;
                                //     } else {
                                //       return false;
                                //     }
                                //   }
                                // }}

                                error={
                                  errors.discountEndDate &&
                                  `${errors.discountEndDate?.message}`
                                }
                                label={t("discountEndDate")}
                                // withAsterisk
                              />
                            )}
                          />
                        </div>

                        <div className="flex w-full space-x-4  justify-between">
                          <Textarea
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            error={errors.notes && `${errors?.notes?.message}`}
                            // withAsterisk
                            label={t("notes")}
                            placeholder={t("notes")}
                            {...register("notes")}
                          />

                          <Controller
                            control={control}
                            name="paymentDueDate"
                            disabled={userInfo?.role?.key === "tenant"}
                            render={({ field }) => (
                              <DateInput
                                icon={<IconCalendar size={16} />}
                                className="w-full "
                                {...field}
                                // excludeDate={(edate) => {
                                //   if (edate.getFullYear() < date.getFullYear()) {
                                //     return true;
                                //   } else if (
                                //     edate.getFullYear() === date.getFullYear() &&
                                //     edate.getMonth() < date.getMonth()
                                //   ) {
                                //     return true;
                                //   } else {
                                //     if (
                                //       edate.getFullYear() <= date.getFullYear() &&
                                //       edate.getMonth() === date.getMonth() &&
                                //       edate.getDate() <= date.getDate()
                                //     ) {
                                //       return true;
                                //     } else {
                                //       return false;
                                //     }
                                //   }
                                // }}

                                error={
                                  errors.paymentDueDate &&
                                  `${errors.paymentDueDate?.message}`
                                }
                                label={t("paymentDueDate")}
                                // withAsterisk
                              />
                            )}
                          />
                        </div>
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>

                <div className="w-full flex justify-end items-center">
                  <Group position="right" mt="xl">
                    {userInfo?.role?.key !== "tenant" && editMode === "new" && (
                      <>
                        <Button
                          classNames={{ label: "flex space-x-1 " }}
                          variant="default"
                          className="dark:text-white hover:bg-primary hover:dark:bg_dark_primary hover:text-white"
                          type="reset"
                          size="xs"
                        >
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="fill-current h-6"
                              viewBox="0 0 32 32"
                            >
                              <path d="M18.90625 4.09375C18.101563 4.09375 17.265625 4.367188 16.625 4.9375L16.625 4.96875L16.59375 5L4.90625 16.59375C3.695313 17.804688 3.703125 19.777344 4.84375 21.0625L4.875 21.09375L4.90625 21.09375L10.90625 27.09375C11.511719 27.699219 12.320313 27.992188 13.125 28L28 28L28 26L16.5 26L27 15.5C28.265625 14.234375 28.304688 12.210938 27.09375 11L21.09375 5C20.488281 4.394531 19.710938 4.09375 18.90625 4.09375 Z M 18.875 6.125C19.195313 6.125 19.492188 6.210938 19.6875 6.40625L25.6875 12.40625C26.074219 12.792969 26.128906 13.558594 25.59375 14.09375L20.5625 19.125L12.90625 11.46875L17.96875 6.4375L18 6.40625C18.253906 6.195313 18.570313 6.125 18.875 6.125 Z M 11.46875 12.90625L19.125 20.5625L14.03125 25.65625C14.019531 25.664063 14.011719 25.679688 14 25.6875C13.484375 26.117188 12.691406 26.066406 12.3125 25.6875L6.34375 19.75C6.328125 19.730469 6.328125 19.707031 6.3125 19.6875C5.902344 19.171875 5.9375 18.375 6.3125 18Z" />
                            </svg>
                          </span>
                          <span>{t("reset")}</span>
                        </Button>
                        <Button
                          variant="filled"
                          className="bg-primary text-white"
                          type="submit"
                          classNames={{ label: "flex space-x-1" }}
                          size={"xs"}
                          loading={newLease.isLoading}
                        >
                          <span>
                            <IconDeviceFloppy size={22} strokeWidth={1.5} />
                          </span>
                          <span>{t("submit")}</span>
                        </Button>
                      </>
                    )}
                    {editMode === "detail" && (
                      <>
                        {(params?.type === "archived" &&
                          archivedLease?.data?.deletedAt) ||
                        (params?.type === "active" &&
                          lease?.data?.deletedAt) ? (
                          <>
                            {userInfo?.role?.key !== "tenant" && (
                              <>
                                <Confirmation
                                  type={"danger"}
                                  message={t("permanentDeleteMessage")}
                                  onYes={() => deleteLease(`${params?.id}`)}
                                  header={t("permanentDeleteTitle")}
                                >
                                  <Button
                                    variant="filled"
                                    loading={deleteResponse.isLoading}
                                    className={`bg-danger text-white flex space-x-2 items-center`}
                                    type="button"
                                    classNames={{ label: "flex space-x-1" }}
                                    size={"xs"}
                                  >
                                    <IconTrash size={20} strokeWidth={1.5} />
                                    <span>{t("delete")}</span>{" "}
                                  </Button>
                                </Confirmation>
                                <Confirmation
                                  type={"notify"}
                                  message={t("restoreMessage")}
                                  onYes={() => restoreLease(`${params?.id}`)}
                                  header={t("restoreTitle")}
                                >
                                  <Button
                                    variant="filled"
                                    loading={restoredLease.isLoading}
                                    className={`bg-green-500 text-white flex space-x-2 items-center`}
                                    type="button"
                                    classNames={{ label: "flex space-x-1" }}
                                    size={"xs"}
                                  >
                                    <IconArrowBackUp
                                      size={20}
                                      strokeWidth={1.5}
                                    />
                                    <span>{t("restore")}</span>
                                  </Button>
                                </Confirmation>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {" "}
                            {((userInfo?.role?.key !== "tenant" &&
                              lease?.data?.leaseStatus ===
                                LeaseStatus.Expired &&
                              dayjs(lease?.data?.internalEndDate).isBefore(
                                dayjs(),
                              )) ||
                              (userInfo?.role?.key !== "tenant" &&
                                lease?.data?.leaseStatus ===
                                  LeaseStatus.Completed)) && (
                              <Button
                                variant="filled"
                                // loading={payManuallyResponse.isLoading}
                                className={`bg-green-500 text-white flex space-x-2 items-center`}
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/lease/new?leaseId=${
                                      params?.id as string
                                    }`,
                                  )
                                }
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                              >
                                <IconCurrencyDollar
                                  size={20}
                                  strokeWidth={1.5}
                                />
                                <span>Renew</span>{" "}
                              </Button>
                            )}
                            {(lease?.data?.leaseStatus ===
                              LeaseStatus.Pending ||
                              (lease?.data?.leaseStatus ===
                                LeaseStatus.Expired &&
                                dayjs(lease?.data?.paymentDueDate).isBefore(
                                  dayjs(),
                                ) &&
                                dayjs(lease?.data?.internalEndDate).isAfter(
                                  dayjs(),
                                ))) && (
                              <Confirmation
                                type={"notify"}
                                message={
                                  userInfo?.role?.key === "tenant"
                                    ? "Are you sure you want to perform manual payment on this lease? If so, please upload a payment proof below."
                                    : "Are you sure you want to perform manual payment on this lease?"
                                }
                                header={`Pay Manually`}
                                yesText={"Pay Manually"}
                                onYes={(reason: string) => {
                                  if (userInfo?.role?.key === "tenant") {
                                    if (fileInput) {
                                      const formData = new FormData()
                                      formData.set(
                                        "leaseId",
                                        lease?.data?.id as string,
                                      )

                                      if (paymentFrequency) {
                                        formData.set(
                                          "paymentFrequency",
                                          paymentFrequency,
                                        )
                                      }
                                      formData.set("fsNumber", fsNumber)
                                      formData.set("file", fileInput as File)
                                      payTenantManually(formData)
                                    }
                                  } else {
                                    let data: {
                                      leaseId: string
                                      paymentFrequency?: PaymentFrequency
                                      fsNumber: string
                                    } = {
                                      leaseId: lease?.data?.id as string,
                                      fsNumber,
                                    }

                                    if (paymentFrequency) {
                                      data.paymentFrequency = paymentFrequency
                                    }
                                    payManually(data)
                                  }
                                }}
                                acceptsFile={userInfo?.role?.key === "tenant"}
                                onAcceptingFile={(item: File) => {
                                  if (userInfo?.role?.key === "tenant") {
                                    setFileInput(item)
                                  }
                                }}
                                acceptsPayment={true}
                                paymentLabel="Payment Frequency"
                                onAcceptingPayment={(
                                  paymentFrequency: PaymentFrequency,
                                ) => {
                                  setPaymentFrequency(paymentFrequency)
                                }}
                                onAcceptingFsNumber={(fsNumber: string) => {
                                  setFsNumber(fsNumber)
                                }}
                              >
                                <Button
                                  variant="filled"
                                  loading={
                                    payManuallyResponse.isLoading ||
                                    payTenantManuallyResponse.isLoading
                                  }
                                  className={`bg-green-500 text-white flex space-x-2 items-center`}
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                >
                                  <IconCurrencyDollar
                                    size={20}
                                    strokeWidth={1.5}
                                  />
                                  <span>Pay Manually</span>{" "}
                                </Button>
                              </Confirmation>
                            )}
                            {userInfo?.role?.key !== "tenant" && (
                              <>
                                <Confirmation
                                  type={"notify"}
                                  message={t("archiveMessage", {
                                    model: t("lease", { count: 1 }),
                                  })}
                                  header={t("archiveTitle", {
                                    name: lease?.data?.tenant?.name,
                                  })}
                                  result={"single"}
                                  resultRequired={true}
                                  selectorLabel={t("selectAReason")}
                                  labelDescription={t("archiveWhy", {
                                    model: t("lease", { count: 1 }),
                                  })}
                                  options={Constants(t)?.ArchiveReason?.map(
                                    (reason) => {
                                      return { label: reason, value: reason }
                                    },
                                  )}
                                  customInput={true}
                                  yesText={t("archive")}
                                  onYes={(reason: string) => {
                                    archiveLease({
                                      id: `${params.id}`,
                                      reason: reason,
                                    })
                                  }}
                                >
                                  <Button
                                    variant="filled"
                                    loading={archiveResponse.isLoading}
                                    className="bg-danger text-white"
                                    type="button"
                                    classNames={{ label: "flex space-x-1" }}
                                    size={"xs"}
                                  >
                                    <span>
                                      <IconArchive
                                        size={22}
                                        strokeWidth={1.5}
                                      />
                                    </span>
                                    <span>{t("archive")}</span>
                                  </Button>
                                </Confirmation>
                                <Button
                                  variant="filled"
                                  className="bg-primary text-white"
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                  onClick={open}
                                  // loading={updateResponse.isLoading}
                                >
                                  <span>
                                    <IconStatusChange
                                      size={22}
                                      strokeWidth={1.5}
                                    />
                                  </span>
                                  <span>{t("changeStatus")}</span>
                                </Button>
                                <Button
                                  variant="filled"
                                  className="bg-primary text-white"
                                  type="submit"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                  loading={updateResponse.isLoading}
                                >
                                  <span>
                                    <IconDeviceFloppy
                                      size={22}
                                      strokeWidth={1.5}
                                    />
                                  </span>
                                  <span>{t("update")}</span>
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Group>
                </div>
              </form>
            </div>
          </Tabs.Panel>

          {editMode == "detail" && (
            <>
              <Tabs.Panel value="rent-increase" pt="xs">
                <RentIncreaseComponent />
              </Tabs.Panel>
              <Tabs.Panel value="management-fee-increase" pt="xs">
                <ManagementFeeIncreaseComponent />
              </Tabs.Panel>
              <Tabs.Panel value="payment" pt="xs">
                <PaymentComponent />
              </Tabs.Panel>
            </>
          )}
        </Tabs>
      </>

      {selectedFiles && selectedFiles.length > 0 && (
        <FileViewerModal
          open={viewOpen}
          close={viewClose}
          fileType={t("lease", { count: 1 })}
          files={selectedFiles as string[]}
          opened={viewOpened}
          remove={handleFileRemoval}
          removeLoading={removeLeaseFileResponse?.isLoading}
          fileNames={lease?.data?.leaseFilenames as string[]}
        />
      )}

      <Modal
        opened={opened}
        onClose={() => {
          close()
        }}
        // className="my-modal-content"
        withCloseButton={false}
        title={t("changeLeaseStatus")}
        padding={20}
        size={"md"}
      >
        <form onSubmit={handleSubmit(onSubmit, onError)} className={"mt-2"}>
          <div className="w-full">
            <div className="flex w-full space-x-4  justify-center">
              <Select
                radius={"xs"}
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                // error={errors?.method && `${errors?.method?.message}`}
                label={t("leaseStatus")}
                dropdownPosition="bottom"
                placeholder={t("leaseStatus")}
                data={_parseLeaseStatus()}
                value={leaseStatus}
                onChange={(val: LeaseStatus) => setLeaseStatus(val)}
                withAsterisk
              />
            </div>
          </div>
          <Group position="right" mt="xl">
            <>
              <Button
                variant="filled"
                className="bg-primary text-white"
                type="button"
                classNames={{ label: "flex space-x-1" }}
                size={"xs"}
                loading={toggleStatusResponse.isLoading}
                onClick={() =>
                  toggleStatus({
                    id: params?.id as string,
                    status: leaseStatus,
                  }).then(() => close())
                }
              >
                <span>
                  <IconDeviceFloppy size={22} strokeWidth={1.5} />
                </span>
                <span>{t("submit")}</span>
              </Button>
            </>
          </Group>
        </form>
      </Modal>
    </ActivityLogWrapperComponent>
    // </div >
  )
}
